/**
 * Чистая логика DataTableV2 (без React) — вынесена для jest-тестов.
 *
 * Семантика повторяет движок, который V2 заменяет:
 *  - сортировка/поиск/суммы — как у dx IntegratedSorting/IntegratedFiltering
 *    (значение колонки = getCellValue(row) ?? row[name]);
 *  - суммы showSum — 1:1 с CustomDataTable (исходное row[name] + sortValueConverter);
 *  - окно пагинации — как DataTablePagination (dx-стиль, 3 кнопки + ellipsis).
 */
import type { HeaderFilterOption } from './HeaderFilter';

export const DEFAULT_COLUMN_WIDTH = 200;
export const DEFAULT_MIN_WIDTH = 150;
/** Минимум колонки при интерактивном ресайзе без явного minWidth — как dx TableColumnResizing. */
export const DEFAULT_RESIZE_MIN_WIDTH = 45;
export const PAGE_SIZES = [5, 10, 20, 50, 100];

/** Колонка в том виде, в котором её присылают страницы (индексная сигнатура — как DataTableColumn). */
export interface InputColumn {
  name: string;
  title?: string;
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  align?: string;
  /** CustomDataTable-диалект фиксированных страниц: выравнивание задаётся textAlign. */
  textAlign?: string;
  visible?: boolean;
  togglingEnabled?: boolean;
  wordWrapEnabled?: boolean;
  fixed?: boolean | string;
  getCellValue?: (row: any, columnName?: string) => any;
  filterPredicate?: (value: any, filter: any, row: any) => boolean;
  sortValueConverter?: (value: any) => any;
  options?: HeaderFilterOption[];
  optionsValue?: HeaderFilterOption[];
  optionsFilter?: (selected: HeaderFilterOption[]) => void;
  [key: string]: any;
}

/** Резолвнутая видимая колонка: все ширины в px, sticky-офсеты посчитаны. */
export interface ResolvedColumn {
  name: string;
  title: string;
  width: number;
  minWidth: number;
  /**
   * Минимум для ИНТЕРАКТИВНОГО РЕСАЙЗА: явный minWidth колонки или 45px
   * (дефолт dx TableColumnResizing). Не путать с minWidth — тот откатывается
   * к column.width (семантика CustomDataTable) и зажимал бы ресайз намертво:
   * у колонок width:200 сосед не смог бы сжаться ниже 200.
   */
  resizeMinWidth: number;
  maxWidth: number | null;
  align: string | null;
  fixed: boolean;
  /** Офсет закрепления слева (сумма ширин предыдущих fixed-колонок); 0 для обычных. */
  stickyLeft: number;
  wordWrapEnabled: boolean;
  togglingEnabled: boolean;
  options?: HeaderFilterOption[];
  optionsValue?: HeaderFilterOption[];
  optionsFilter?: (selected: HeaderFilterOption[]) => void;
  /** Исходная колонка (с getCellValue и доменными полями) — для dataCellComponent. */
  source: InputColumn;
}

/** Числовая ширина колонки: number | строка-число; проценты/мусор — fallback. */
export const normalizeWidth = (value: unknown, fallback: number): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? Math.round(value) : fallback;
  }

  const asString = String(value ?? '').trim();

  if (!/^\d+(\.\d+)?$/.test(asString)) {
    return fallback;
  }

  const parsed = Number.parseFloat(asString);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : fallback;
};

/** Значение ячейки по правилам dx Grid: getCellValue(row, name) ?? row[name]. */
export const getCellValue = (column: InputColumn, row: any): any => {
  if (!row) return undefined;
  if (typeof column.getCellValue === 'function') {
    return column.getCellValue(row, column.name);
  }
  return row[column.name];
};

const isNullish = (value: unknown): boolean => value === undefined || value === null || value === '';

/**
 * Сравнение непустых значений: числа численно, строки по ru-локали,
 * смешанные типы — строками. Пустые (null/undefined/'') обрабатывает sortRows:
 * они всегда в конце списка независимо от направления (как NULLS LAST).
 */
export const compareValues = (a: unknown, b: unknown): number => {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return (a ? 1 : 0) - (b ? 1 : 0);
  }

  return String(a).localeCompare(String(b), 'ru');
};

/**
 * Локальная сортировка (localSorting): копия строк, отсортированная по значению
 * колонки + sortValueConverter. Пустые значения — всегда в конце.
 *
 * Значение сортировки — ячейка (getCellValue); если она непримитивна (JSX —
 * например, Link в имени партнёра), берётся исходное row[name] — как сортировал
 * CustomDataTable. Иначе String(JSX)='[object Object]' у всех строк и порядок
 * не менялся вовсе.
 *
 * rawValues=true — семантика CustomDataTable (fixed-страницы): значением считается
 * исходное row[name], а НЕ getCellValue. Важно для отчётов, чьи getCellValue
 * возвращают JSX (LossAdjustmentReport: сортировка по ним бессмысленна).
 */
export const sortRows = (
  rows: any[],
  columns: InputColumn[],
  columnName: string | null | undefined,
  direction: string | null | undefined,
  rawValues = false
): any[] => {
  if (!columnName || rows.length === 0) {
    return rows;
  }

  const column = columns.find(col => col?.name === columnName);
  const desc = String(direction || 'asc').toLowerCase() === 'desc';

  /** Значение сортировки: ячейка; JSX/объект → исходное row[name] (как CustomDataTable). */
  const sortValue = (row: any) => {
    const cell = rawValues
      ? row?.[columnName]
      : getCellValue(column || { name: columnName }, row);

    const isPrimitive = cell === null
      || cell === undefined
      || ['string', 'number', 'boolean'].includes(typeof cell);

    return isPrimitive ? cell : row?.[columnName];
  };

  return [...rows].sort((rowA, rowB) => {
    let valueA = sortValue(rowA);
    let valueB = sortValue(rowB);

    if (column?.sortValueConverter) {
      valueA = column.sortValueConverter(valueA);
      valueB = column.sortValueConverter(valueB);
    }

    const nullishA = isNullish(valueA);
    const nullishB = isNullish(valueB);

    if (nullishA || nullishB) {
      // пустые — в конец независимо от направления
      return nullishA === nullishB ? 0 : (nullishA ? 1 : -1);
    }

    const comparison = compareValues(valueA, valueB);
    return desc ? -comparison : comparison;
  });
};

/**
 * Локальный поиск (searchValue, семантика dx IntegratedFiltering + SearchState):
 * регистронезависимое вхождение в значение любой колонки (включая скрытые —
 * как в dx: TableColumnVisibility не убирает колонку из фильтрации).
 */
/** Фильтр колонки (FilteringState / поиск в семантике dx IntegratedFiltering). */
export interface ColumnFilter {
  columnName: string;
  value?: any;
  operation?: string;
}

/** Операции фильтров — 1-в-1 с operationPredicates dx-grid-core. */
const OPERATION_PREDICATES: Record<string, (value: any, filter: ColumnFilter) => boolean> = {
  contains: (value, filter) => String(value).toLowerCase().includes(String(filter.value).toLowerCase()),
  notContains: (value, filter) => !String(value).toLowerCase().includes(String(filter.value).toLowerCase()),
  startsWith: (value, filter) => String(value).toLowerCase().startsWith(String(filter.value).toLowerCase()),
  endsWith: (value, filter) => String(value).toLowerCase().endsWith(String(filter.value).toLowerCase()),
  equal: (value, filter) => String(value) === String(filter.value),
  notEqual: (value, filter) => String(value) !== String(filter.value),
  greaterThan: (value, filter) => value > filter.value,
  greaterThanOrEqual: (value, filter) => value >= filter.value,
  lessThan: (value, filter) => value < filter.value,
  lessThanOrEqual: (value, filter) => value <= filter.value,
};

/**
 * Фильтрация строк — семантика dx IntegratedFiltering:
 *  - фильтры панели (FilteringState) комбинируются через AND;
 *  - поиск (SearchState) — фильтр {value} по КАЖДОЙ колонке через OR
 *    (searchFilterExpression из dx-grid-core);
 *  - каждый фильтр оценивается filterPredicate колонки, если задан
 *    (например, у партнёров — RegExp по props.children JSX-ячейки),
 *    иначе операцией contains (регистронезависимая подстрока).
 */
const matchesColumnFilter = (
  column: InputColumn,
  row: any,
  filter: ColumnFilter
): boolean => {
  const value = getCellValue(column, row);

  if (typeof column.filterPredicate === 'function') {
    return !!column.filterPredicate(value, filter, row);
  }

  const predicate = OPERATION_PREDICATES[filter.operation ?? 'contains'] ?? OPERATION_PREDICATES.contains;

  return predicate(value, filter);
};

export const filterRows = (
  rows: any[],
  columns: InputColumn[],
  filters: ColumnFilter[] | null | undefined,
  searchValue: string | null | undefined
): any[] => {
  const columnByName = new Map(columns.map(column => [column.name, column]));
  const columnFilters = (filters ?? []).filter(filter => columnByName.has(filter.columnName));
  const query = String(searchValue ?? '').trim();

  if ((columnFilters.length === 0 && !query) || rows.length === 0) {
    return rows;
  }

  return rows.filter(row => (
    columnFilters.every(filter => matchesColumnFilter(columnByName.get(filter.columnName)!, row, filter))
    && (!query || columns.some(column => matchesColumnFilter(
      column,
      row,
      { columnName: column.name, value: searchValue }
    )))
  ));
};

/** Парсинг «числа из ячейки» для суммы — 1:1 с CustomDataTable. */
const parseSumNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.,-]/g, '').replace(',', '.');
    return Number.parseFloat(cleaned);
  }

  return Number.parseFloat(value as any);
};

/**
 * Сумма колонки страницы (showSum) — семантика CustomDataTable: исходное
 * row[name] (НЕ getCellValue) + sortValueConverter, исключение строк через
 * shouldIncludeRowInSum, округление результата.
 */
export const sumColumn = (
  rows: any[],
  column: InputColumn,
  shouldIncludeRowInSum?: ((row: any, columnName?: string) => boolean) | null
): number => {
  const total = rows.reduce<number>((acc, row) => {
    if (!row) return acc;

    if (shouldIncludeRowInSum && !shouldIncludeRowInSum(row, column.name)) {
      return acc;
    }

    let value = row[column.name];

    if (typeof column.sortValueConverter === 'function') {
      value = column.sortValueConverter(value);
    }

    const num = parseSumNumber(value);
    return acc + (Number.isNaN(num) ? 0 : num);
  }, 0);

  return Math.round(total);
};

/** Диапазон строк окна виртуализации (0-based, end эксклюзивный). */
export const visibleRange = (
  scrollTop: number,
  viewportHeight: number,
  rowHeight: number,
  totalCount: number,
  overscan = 10
): { start: number; end: number } => {
  if (totalCount === 0 || rowHeight <= 0) {
    return { start: 0, end: 0 };
  }

  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const end = Math.min(
    totalCount,
    Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan
  );

  return { start, end: Math.max(end, start + 1) };
};

export interface ResolveColumnsOptions {
  columns?: InputColumn[] | null;
  /** Скрытые колонки (defaultHiddenColumnNames из настроек страницы). */
  hiddenColumnNames?: string[] | null;
  /** Ширины после интерактивного ресайза (px). */
  columnWidths?: Record<string, number> | null;
  /** При сохранённых настройках visible:false не прячет колонку (логика dx). */
  isSettingsStored?: boolean;
  /** Дефолт выравнивания (CustomDataTable-диалект: textAlign || 'right'). */
  defaultAlign?: string | null;
  /** Custom-диалект: visible:false НЕ прячет колонку — CustomDataTable флаг игнорировал. */
  ignoreVisibleFlag?: boolean;
}

/**
 * Видимые колонки с посчитанными ширинами и sticky-офсетами.
 * Ширина: ресайз → column.width → 200; stickyLeft = сумма ширин предыдущих fixed.
 * Выравнивание: align ?? textAlign (CustomDataTable-диалект) ?? defaultAlign.
 */
export const resolveColumns = ({
  columns,
  hiddenColumnNames,
  columnWidths,
  isSettingsStored,
  defaultAlign,
  ignoreVisibleFlag,
}: ResolveColumnsOptions): ResolvedColumn[] => {
  const hidden = new Set(hiddenColumnNames ?? []);

  const resolved: ResolvedColumn[] = [];

  (columns ?? []).forEach(column => {
    if (!column || !column.name) return;
    if (hidden.has(column.name)) return;
    // visible:false прячет колонку только без сохранённых настроек — как dx
    // (при сохранённых набор скрытых приходит из defaultHiddenColumnNames);
    // custom-диалект (CustomDataTable) флаг всегда игнорировал
    if (!ignoreVisibleFlag && column.visible === false && !isSettingsStored) return;

    // minWidth откатывается к column.width (семантика CustomDataTable):
    // узкая колонка не должна расширяться до дефолтных 150px
    const minWidth = normalizeWidth(column.minWidth ?? column.width, DEFAULT_MIN_WIDTH);
    const resizeMinWidth = normalizeWidth(column.minWidth, DEFAULT_RESIZE_MIN_WIDTH);
    const maxWidth = column.maxWidth != null ? normalizeWidth(column.maxWidth, minWidth) : null;
    const fallbackWidth = normalizeWidth(column.width, DEFAULT_COLUMN_WIDTH);
    const resized = columnWidths?.[column.name];
    // ресайзнутая ширина живёт по ресайз-минимуму (45px как dx) — clamp по
    // minWidth=width вернул бы колонку к исходной ширине на следующем рендере
    const width = Number.isFinite(resized) && (resized as number) > 0
      ? Math.max(resized as number, resizeMinWidth)
      : Math.max(fallbackWidth, minWidth);

    resolved.push({
      name: column.name,
      title: column.title ?? column.name,
      width,
      minWidth,
      resizeMinWidth,
      maxWidth,
      align: column.align ?? column.textAlign ?? defaultAlign ?? null,
      fixed: Boolean(column.fixed),
      stickyLeft: 0,
      wordWrapEnabled: Boolean(column.wordWrapEnabled),
      togglingEnabled: column.togglingEnabled !== false,
      options: column.options,
      optionsValue: column.optionsValue,
      optionsFilter: column.optionsFilter,
      source: column,
    });
  });

  let sticky = 0;
  resolved.forEach(column => {
    if (column.fixed) {
      column.stickyLeft = sticky;
      sticky += column.width;
    }
  });

  return resolved;
};

/** Текущая страница с защитой от выхода за пределы (данные ужались/фильтр). */
export const clampPage = (page: number, totalPages: number): number => (
  Math.max(0, Math.min(Math.trunc(page) || 0, Math.max(0, totalPages - 1)))
);

/** Часть текста ячейки: match=true — вхождение поискового запроса (рендерится <mark>). */
export interface HighlightPart {
  text: string;
  match: boolean;
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Разбивка текста ячейки для подсветки быстрого поиска: все вхождения запроса
 * (регистронезависимо) помечаются match=true. null — подсветка не нужна
 * (пустой запрос или вхождений нет), ячейка рендерится как есть.
 *
 * Поиск регуляркой по ИСХОДНОЙ строке (а не по toLowerCase-копии): у пары
 * юникод-символов нижний регистр меняет длину, и индексы поехали бы.
 */
export const splitHighlightParts = (text: string, query: string): HighlightPart[] | null => {
  const prepared = String(query ?? '').trim();

  if (!prepared) {
    return null;
  }

  const regexp = new RegExp(escapeRegExp(prepared), 'gi');
  const parts: HighlightPart[] = [];
  let lastIndex = 0;
  let match = regexp.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, match.index), match: false });
    }

    parts.push({ text: match[0], match: true });
    lastIndex = match.index + match[0].length;
    match = regexp.exec(text);
  }

  if (parts.length === 0) {
    return null;
  }

  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex), match: false });
  }

  return parts;
};

export const formatInt = (value: number): string => (
  Number.isFinite(value) ? value.toLocaleString('ru-RU') : ''
);
